import { FC, useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useTranslations } from 'hooks/useTranslations';
import { fetchDocumentState, documentSelector } from 'store/documentState';
import { getMarketSettings } from 'utils/marketSettingUtils';

import { IconType } from 'components/UI/Icon';
import PageState from 'components/UI/Page/PageState';
import SeoHelmet from 'components/fragments/SeoHelmet';
import PageWrapper from 'components/UI/Page/PageWrapper';
import RenderMarkdown from 'components/fragments/RenderMarkdown';
import Copyright from 'components/UI/Copyright';
import Loader from 'components/UI/Loader';

export const TermsOfUsePage: FC = () => {
  const dispatch = useAppDispatch();
  const t = useTranslations();

  useEffect(() => {
    const { languageCode, languageRegionCode } = getMarketSettings();
    dispatch(fetchDocumentState(languageCode, languageRegionCode));
  }, [dispatch]);

  const { termsOfUseDocumentUrl, isLoading } = useAppSelector(documentSelector);

  // Content
  const content = useMemo(() => {
    // Loading
    if (isLoading) {
      return <Loader padding center size="large" />;
    }

    // Empty state
    if (!termsOfUseDocumentUrl) {
      return (
        <PageState
          iconType={IconType.Document}
          title={t('terms_of_use.empty_title')}
          description={t('terms_of_use.empty_description')}
        />
      );
    }

    // Render document
    return <RenderMarkdown documentUrl={termsOfUseDocumentUrl} />;
  }, [isLoading, termsOfUseDocumentUrl, t]);

  return (
    <PageWrapper>
      <Copyright>
        <SeoHelmet title="terms_of_use.meta_title" />
        {content}
      </Copyright>
    </PageWrapper>
  );
};
